'use client';

import { useState } from 'react';
import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  DotsThreeVerticalIcon,
  FunnelIcon,
  ListMagnifyingGlassIcon,
  PencilSimpleIcon,
  TargetIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import type { CategoryOverviewRow } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';

export interface CategoryRowActions {
  onEdit: (row: CategoryOverviewRow) => void;
  onEditLimit: () => void;
  onViewTransactions: (row: CategoryOverviewRow) => void;
  onViewRules: (row: CategoryOverviewRow) => void;
  onToggleArchived: (row: CategoryOverviewRow) => void;
}

interface CategoryActionsMenuProps extends CategoryRowActions {
  row: CategoryOverviewRow;
  /** Archived categories cannot receive limits. */
  canEditLimit: boolean;
  /**
   * Drops the limit item instead of disabling it: for someone who can never set one here (a space viewer, a partner
   * preview, the uncategorized bucket) a greyed-out "Definir limite" is an offer that cannot be taken.
   */
  hideLimit?: boolean;
  /** The limit item's sublabel; the personal page's limits are monthly. */
  limitHint?: string;
  /** "Regras desta categoria" only shows when the category has any. */
  hasRules: boolean;
  /** Income rows: open the meta picker (link/alter/remove). */
  onLinkGoal?: () => void;
}

interface ActionItem {
  key: string;
  label: string;
  sublabel: string;
  icon: PhosphorIcon;
  disabled?: boolean;
  destructive?: boolean;
  run: () => void;
}

// The mockup's kebab (.kebab → MENU_HTML / mobile actSheet): Editar categoria · Alterar
// limite · Ver lançamentos · Regras desta categoria · Arquivar. Edit/archive apply to
// custom categories.
export function CategoryActionsMenu({
  row,
  canEditLimit,
  hideLimit = false,
  limitHint = 'Limite mensal da categoria',
  hasRules,
  onEdit,
  onEditLimit,
  onLinkGoal,
  onViewTransactions,
  onViewRules,
  onToggleArchived,
}: CategoryActionsMenuProps) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  const items: ActionItem[] = [];
  if (row.source === 'custom') {
    items.push({
      key: 'edit',
      label: 'Editar categoria',
      sublabel: 'Nome, cor e ícone',
      icon: PencilSimpleIcon,
      run: () => onEdit(row),
    });
  }
  // Income/neutral categories never carry limits — the item disappears instead of
  // rendering permanently disabled.
  if (row.kind === 'expense' && !hideLimit) {
    items.push({
      key: 'limit',
      label: row.limit == null ? 'Definir limite' : 'Alterar limite',
      sublabel: limitHint,
      icon: WalletIcon,
      disabled: !canEditLimit || row.isArchived,
      run: onEditLimit,
    });
  }
  // Receitas carry a meta link where despesas carry a limit.
  if (row.kind === 'income' && onLinkGoal) {
    items.push({
      key: 'goal',
      label: row.linkedGoal == null ? 'Vincular à meta' : 'Alterar meta vinculada',
      sublabel: 'Progresso da meta na linha da receita',
      icon: TargetIcon,
      disabled: row.isArchived,
      run: onLinkGoal,
    });
  }
  items.push({
    key: 'transactions',
    label: 'Ver lançamentos',
    sublabel: 'Detalhe da categoria no período',
    icon: ListMagnifyingGlassIcon,
    run: () => onViewTransactions(row),
  });
  if (hasRules) {
    items.push({
      key: 'rules',
      label: 'Regras desta categoria',
      sublabel: 'Categorização automática por estabelecimento',
      icon: FunnelIcon,
      run: () => onViewRules(row),
    });
  }
  if (row.source === 'custom') {
    items.push(row.isArchived
      ? {
          key: 'unarchive',
          label: 'Desarquivar',
          sublabel: 'Volta aos seletores e à criação de regras',
          icon: ArrowCounterClockwiseIcon,
          run: () => onToggleArchived(row),
        }
      : {
          key: 'archive',
          label: 'Arquivar',
          sublabel: 'Histórico preservado; some dos seletores',
          icon: ArchiveIcon,
          destructive: true,
          run: () => onToggleArchived(row),
        });
  }

  const trigger = (
    <button
      type="button"
      aria-label={`Ações de ${row.name}`}
      className="grid size-8 shrink-0 place-items-center rounded-lg text-text-tertiary transition-colors hover:bg-accent hover:text-text-primary"
      onClick={isMobile ? () => setSheetOpen(true) : undefined}
    >
      <DotsThreeVerticalIcon className="size-4.5" weight="bold" />
    </button>
  );

  if (isMobile) {
    // Mockup's actSheet: bottom sheet of icon + label + sublabel rows.
    return (
      <>
        {trigger}
        <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <DrawerTitle>{row.name}</DrawerTitle>
              <DrawerDescription className="sr-only">Ações da categoria {row.name}.</DrawerDescription>
            </DrawerHeader>
            <div className="flex flex-col gap-1 px-2 pb-4">
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  disabled={item.disabled}
                  onClick={() => {
                    setSheetOpen(false);
                    item.run();
                  }}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                    item.destructive ? 'text-error-text' : 'text-text-primary'
                  } hover:bg-accent`}
                >
                  <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${
                    item.destructive ? 'bg-error-surface text-error-text' : 'bg-muted text-text-secondary'
                  }`}>
                    <item.icon className="size-4.5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{item.label}</span>
                    <span className="block text-xs text-text-tertiary">{item.sublabel}</span>
                  </span>
                </button>
              ))}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-60">
        {items.map((item, i) => (
          <div key={item.key}>
            {item.destructive && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              disabled={item.disabled}
              variant={item.destructive ? 'destructive' : 'default'}
              className="cursor-pointer gap-2"
              onSelect={item.run}
            >
              <item.icon className="size-4" />
              {item.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
